import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Upload, FileText, FileSpreadsheet, FileImage, FileArchive, Download, Eye,
  Trash2, Search, Calendar, RefreshCw, Loader2, CheckCircle2, AlertCircle,
  FolderOpen, Filter, X, Sparkles, TrendingUp,
} from "lucide-react";
import AdminLayout from "@/components/admin/AdminLayout";
import { adminApi } from "@/lib/api";
import { Toaster, toast } from "sonner";

const NEXON = { indigo: "#636AC3", ink: "#1E1E3F", mint: "#C9E9A4", cloud: "#FAF9FE", ash: "#7A7A99" };

const BYTE_UNITS = ["B", "KB", "MB", "GB"];
function fmtSize(bytes) {
  if (!bytes) return "0 B";
  let n = bytes, i = 0;
  while (n >= 1024 && i < BYTE_UNITS.length - 1) { n /= 1024; i += 1; }
  return `${n.toFixed(n >= 10 || i === 0 ? 0 : 1)} ${BYTE_UNITS[i]}`;
}
const fmtDate = (d) => {
  if (!d) return "—";
  try { return new Date(`${d}T00:00:00`).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" }); }
  catch { return d; }
};
const fmtDateTime = (iso) => {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString(); } catch { return iso; }
};
function iconForFile(filename, ct) {
  const ext = (filename || "").split(".").pop()?.toLowerCase() || "";
  if (["xlsx","xls","xlsm","csv","tsv"].includes(ext)) return FileSpreadsheet;
  if (["png","jpg","jpeg","webp","gif"].includes(ext)) return FileImage;
  if (["zip","rar","7z"].includes(ext)) return FileArchive;
  if ((ct || "").startsWith("image/")) return FileImage;
  return FileText;
}
const toneFor = (filename) => {
  const ext = (filename || "").split(".").pop()?.toLowerCase() || "";
  if (["xlsx","xls","csv","tsv","xlsm"].includes(ext)) return { bg: "#E9F7E1", border: "#C9E9A4", fg: "#3E7A1E" };
  if (["png","jpg","jpeg","webp","gif"].includes(ext)) return { bg: "#FFF4DA", border: "#F4D48A", fg: "#8B6A11" };
  if (["zip","rar","7z"].includes(ext)) return { bg: "#FDECEF", border: "#F7B5C0", fg: "#B22C40" };
  if (ext === "pdf") return { bg: "#FEE2E2", border: "#F1A5A5", fg: "#B12525" };
  return { bg: "#E7E8F6", border: "#C7C9E7", fg: "#3B3F86" };
};
function todayLocal() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,"0")}-${String(d.getDate()).padStart(2,"0")}`;
}

export default function AdminVendorReports() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [q, setQ] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [files, setFiles] = useState([]);
  const [reportDate, setReportDate] = useState(todayLocal());
  const [label, setLabel] = useState("");
  const [notes, setNotes] = useState("");
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState({ current: 0, total: 0 });
  const [dragOver, setDragOver] = useState(false);
  const fileInputRef = useRef(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (q.trim()) params.q = q.trim();
      if (dateFrom) params.date_from = dateFrom;
      if (dateTo) params.date_to = dateTo;
      const r = await adminApi.get("/admin/vendor-reports", { params });
      setItems(r.data || []);
    } catch {
      toast.error("Couldn't load vendor reports");
      setItems([]);
    } finally { setLoading(false); }
  }, [q, dateFrom, dateTo]);

  useEffect(() => { load(); }, [load]);

  const resetForm = () => {
    setFiles([]); setLabel(""); setNotes(""); setReportDate(todayLocal());
    setUploadProgress({ current: 0, total: 0 });
    if (fileInputRef.current) fileInputRef.current.value = "";
  };
  const removeFile = (idx) => setFiles((prev) => prev.filter((_, i) => i !== idx));
  const onDrop = (e) => {
    e.preventDefault(); setDragOver(false);
    const dropped = Array.from(e.dataTransfer.files || []);
    if (dropped.length) setFiles((prev) => [...prev, ...dropped]);
  };

  const onUpload = async (e) => {
    e?.preventDefault?.();
    if (!files.length) { toast.error("Pick at least one file"); return; }
    if (!reportDate) { toast.error("Pick a report date"); return; }
    const oversized = files.find((f) => f.size > 25 * 1024 * 1024);
    if (oversized) { toast.error(`"${oversized.name}" exceeds 25 MB`); return; }

    setUploading(true);
    setUploadProgress({ current: 0, total: files.length });
    let succeeded = 0; const failures = [];
    for (let i = 0; i < files.length; i++) {
      const f = files[i];
      setUploadProgress({ current: i + 1, total: files.length });
      const fd = new FormData();
      fd.append("file", f);
      fd.append("report_date", reportDate);
      if (label.trim()) fd.append("label", label.trim());
      if (notes.trim()) fd.append("notes", notes.trim());
      try {
        await adminApi.post("/admin/vendor-reports", fd, { headers: { "Content-Type": "multipart/form-data" } });
        succeeded += 1;
      } catch (err) {
        failures.push({ name: f.name, error: err?.response?.data?.detail || "Upload failed" });
      }
    }
    if (succeeded && !failures.length) { toast.success(`Uploaded ${succeeded} report${succeeded===1?"":"s"}`); resetForm(); }
    else if (succeeded && failures.length) { toast.error(`${succeeded} uploaded, ${failures.length} failed (${failures[0].name}: ${failures[0].error})`); setFiles(files.filter((f) => failures.some((x) => x.name === f.name))); }
    else { toast.error(`All ${failures.length} uploads failed: ${failures[0]?.error}`); }
    load(); setUploading(false);
  };

  const onView = async (row) => {
    const win = window.open("about:blank", "_blank");
    if (!win) { toast.error("Pop-up blocked. Allow pop-ups, then click View again."); return; }
    try {
      const r = await adminApi.get(`/admin/vendor-reports/${row.id}/download`, { params: { inline: true }, responseType: "blob" });
      const blob = new Blob([r.data], { type: row.content_type || r.data.type });
      const url = URL.createObjectURL(blob);
      win.location.replace(url);
      try { win.opener = null; } catch { /* cross-origin */ }
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
    } catch { win.close(); toast.error("Couldn't open the report"); }
  };

  const onDownload = async (row) => {
    try {
      const r = await adminApi.get(`/admin/vendor-reports/${row.id}/download`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url; a.download = row.filename || "report";
      document.body.appendChild(a); a.click(); document.body.removeChild(a);
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch { toast.error("Download failed"); }
  };

  const onDelete = async (row) => {
    if (!window.confirm(`Delete "${row.filename}"? This cannot be undone.`)) return;
    try {
      await adminApi.delete(`/admin/vendor-reports/${row.id}`);
      toast.success("Report deleted");
      setItems((prev) => prev.filter((x) => x.id !== row.id));
    } catch { toast.error("Delete failed"); }
  };

  const grouped = useMemo(() => {
    const map = new Map();
    for (const it of items) {
      const key = it.report_date || "—";
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(it);
    }
    return Array.from(map.entries());
  }, [items]);

  const kpi = useMemo(() => {
    const total = items.length;
    const bytes = items.reduce((s, x) => s + (x.size || 0), 0);
    const days = new Set(items.map((x) => x.report_date)).size;
    const today = todayLocal();
    const todayCount = items.filter((x) => x.report_date === today).length;
    return { total, bytes, days, todayCount };
  }, [items]);

  return (
    <AdminLayout>
      <Toaster richColors position="top-center" />
      <div className="p-6 lg:p-8" style={{ backgroundColor: NEXON.cloud }} data-testid="admin-vendor-reports-page">
        {/* Header */}
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <span className="text-[10px] font-black tracking-widest uppercase" style={{ color: NEXON.indigo }}>Vendor reports</span>
            <h1 className="text-3xl lg:text-4xl font-black tracking-tight mt-1" style={{ color: NEXON.ink }}>
              Daily vendor uploads
            </h1>
            <p className="text-sm mt-1" style={{ color: NEXON.ash }}>
              Excel · Word · PDF · CSV · ZIP — stored privately, grouped by date, downloadable any time.
            </p>
            <div className="mt-3 h-[3px] w-24 rounded-full" style={{ backgroundImage: `linear-gradient(90deg, ${NEXON.indigo}, ${NEXON.ink}, ${NEXON.mint})` }} />
          </div>
          <button
            onClick={load}
            disabled={loading}
            data-testid="vendor-reports-refresh"
            className="inline-flex items-center gap-2 h-11 px-4 rounded-xl border border-slate-200 bg-white hover:bg-slate-50 text-sm font-black text-slate-700 disabled:opacity-50"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} /> Refresh
          </button>
        </div>

        {/* KPI row */}
        <div className="mt-6 grid grid-cols-2 lg:grid-cols-4 gap-3">
          {[
            { k: "total", label: "REPORTS ARCHIVED", val: kpi.total, tone: "bg-indigo-100 text-indigo-800", Icon: FolderOpen },
            { k: "today", label: "UPLOADED TODAY", val: kpi.todayCount, tone: "bg-emerald-100 text-emerald-800", Icon: Sparkles },
            { k: "days",  label: "DAYS COVERED",    val: kpi.days,     tone: "bg-amber-100 text-amber-800",   Icon: Calendar },
            { k: "size",  label: "STORAGE USED",    val: fmtSize(kpi.bytes), tone: "bg-slate-100 text-slate-700", Icon: TrendingUp },
          ].map((s) => (
            <div key={s.k} data-testid={`kpi-${s.k}`} className="bg-white rounded-2xl border border-slate-200 p-4">
              <div className="flex items-center justify-between">
                <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-[9px] font-black tracking-widest ${s.tone}`}>{s.label}</span>
                <s.Icon className="w-4 h-4 text-slate-300" />
              </div>
              <div className="mt-2 text-3xl font-black tabular-nums" style={{ color: NEXON.ink }}>{s.val}</div>
            </div>
          ))}
        </div>

        {/* Upload card */}
        <form
          onSubmit={onUpload}
          className="mt-6 bg-white rounded-2xl border border-slate-200 p-5 sm:p-6 space-y-5"
          data-testid="vendor-reports-upload-form"
        >
          <div className="flex items-center justify-between gap-3">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ backgroundColor: NEXON.mint, color: NEXON.ink }}>
                <Upload size={16} />
              </div>
              <div>
                <div className="text-sm font-black" style={{ color: NEXON.ink }}>Upload today's reports</div>
                <div className="text-xs text-slate-500">Each file becomes its own report with the shared date & label.</div>
              </div>
            </div>
          </div>

          {/* Dropzone */}
          <div
            onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
            onDragLeave={() => setDragOver(false)}
            onDrop={onDrop}
            onClick={() => fileInputRef.current?.click()}
            className={`cursor-pointer rounded-xl border-2 border-dashed p-6 text-center transition-colors ${dragOver ? "bg-indigo-50" : "bg-slate-50/60 hover:bg-slate-50"}`}
            style={{ borderColor: dragOver ? NEXON.indigo : "#CBD5E1" }}
            data-testid="vendor-report-dropzone"
          >
            <Upload className="w-6 h-6 mx-auto mb-2" style={{ color: NEXON.indigo }} />
            <div className="text-sm font-black" style={{ color: NEXON.ink }}>
              Drop files here or <span style={{ color: NEXON.indigo }}>browse</span>
            </div>
            <div className="text-xs text-slate-500 mt-1">Excel · Word · PDF · CSV · ZIP · Images — up to 25 MB each</div>
            <input
              ref={fileInputRef}
              type="file"
              multiple
              onChange={(e) => setFiles(Array.from(e.target.files || []))}
              data-testid="vendor-report-file"
              className="hidden"
            />
          </div>

          {/* Metadata grid */}
          <div className="grid sm:grid-cols-12 gap-3">
            <div className="sm:col-span-3">
              <label className="block text-[10px] uppercase tracking-widest font-black text-slate-500 mb-1.5">Report date</label>
              <div className="relative">
                <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" />
                <input
                  type="date" value={reportDate} onChange={(e) => setReportDate(e.target.value)} required
                  data-testid="vendor-report-date"
                  className="w-full h-11 pl-9 pr-3 rounded-xl border border-slate-200 bg-slate-50 focus:bg-white focus:border-indigo-400 focus:ring-2 focus:ring-indigo-100 outline-none text-sm"
                />
              </div>
            </div>
            <div className="sm:col-span-5">
              <label className="block text-[10px] uppercase tracking-widest font-black text-slate-500 mb-1.5">Label / vendor (optional)</label>
              <input
                value={label} onChange={(e) => setLabel(e.target.value)} placeholder="e.g. Vendor A — daily settlement"
                maxLength={200} data-testid="vendor-report-label"
                className="w-full h-11 px-3 rounded-xl border border-slate-200 bg-slate-50 focus:bg-white focus:border-indigo-400 focus:ring-2 focus:ring-indigo-100 outline-none text-sm"
              />
            </div>
            <div className="sm:col-span-4">
              <label className="block text-[10px] uppercase tracking-widest font-black text-slate-500 mb-1.5">Notes (optional)</label>
              <input
                value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Any remarks for later"
                maxLength={2000} data-testid="vendor-report-notes"
                className="w-full h-11 px-3 rounded-xl border border-slate-200 bg-slate-50 focus:bg-white focus:border-indigo-400 focus:ring-2 focus:ring-indigo-100 outline-none text-sm"
              />
            </div>
          </div>

          {/* Selected files */}
          {files.length > 0 && (
            <div className="rounded-xl border border-slate-200 bg-slate-50/60 p-3 space-y-1.5" data-testid="vendor-report-file-list">
              <div className="text-[10px] uppercase tracking-widest font-black text-slate-500">
                {files.length} file{files.length === 1 ? "" : "s"} queued
              </div>
              <ul className="space-y-1">
                {files.map((f, idx) => (
                  <li key={`${f.name}-${idx}`} className="flex items-center gap-2 text-xs text-slate-700">
                    <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600 flex-shrink-0" />
                    <span className="font-mono truncate flex-1">{f.name}</span>
                    <span className="text-slate-400">{fmtSize(f.size)}</span>
                    {!uploading && (
                      <button
                        type="button" onClick={() => removeFile(idx)} aria-label={`Remove ${f.name}`}
                        className="w-5 h-5 inline-flex items-center justify-center rounded text-slate-400 hover:text-rose-600 hover:bg-rose-50"
                      >×</button>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          )}

          <div className="flex items-center justify-between gap-3">
            {files.length === 0 ? (
              <div className="flex items-center gap-2 text-xs text-slate-400">
                <AlertCircle className="w-4 h-4" /> No files selected
              </div>
            ) : uploading ? (
              <div className="flex items-center gap-2 text-xs" style={{ color: NEXON.indigo }}>
                <Loader2 className="w-4 h-4 animate-spin" /> Uploading {uploadProgress.current} of {uploadProgress.total}…
              </div>
            ) : (
              <div className="text-xs text-slate-500">Each file uploads as a separate report sharing the same date / label / notes.</div>
            )}
            <button
              type="submit" disabled={uploading || files.length === 0}
              data-testid="vendor-report-submit"
              className="inline-flex items-center gap-2 h-11 px-5 rounded-xl text-white text-sm font-black disabled:opacity-50 disabled:cursor-not-allowed transition-colors shadow-sm"
              style={{ backgroundColor: NEXON.ink }}
            >
              {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
              {uploading ? `Uploading ${uploadProgress.current}/${uploadProgress.total}…` : files.length > 1 ? `Upload ${files.length} reports` : "Upload report"}
            </button>
          </div>
        </form>

        {/* Filters */}
        <div className="mt-6 bg-white rounded-2xl border border-slate-200 p-4 flex flex-wrap gap-3 items-end">
          <div className="flex-1 min-w-[220px]">
            <label className="block text-[10px] uppercase tracking-widest font-black text-slate-500 mb-1.5">Search</label>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <input
                value={q} onChange={(e) => setQ(e.target.value)}
                placeholder="filename, label, notes, uploader…"
                data-testid="vendor-reports-search"
                className="w-full h-11 pl-10 pr-4 rounded-xl border border-slate-200 bg-slate-50 focus:bg-white focus:border-indigo-400 focus:ring-2 focus:ring-indigo-100 outline-none text-sm"
              />
            </div>
          </div>
          <div>
            <label className="block text-[10px] uppercase tracking-widest font-black text-slate-500 mb-1.5">From</label>
            <input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)}
              data-testid="vendor-reports-date-from"
              className="h-11 px-3 rounded-xl border border-slate-200 bg-slate-50 focus:bg-white focus:border-indigo-400 outline-none text-sm" />
          </div>
          <div>
            <label className="block text-[10px] uppercase tracking-widest font-black text-slate-500 mb-1.5">To</label>
            <input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)}
              data-testid="vendor-reports-date-to"
              className="h-11 px-3 rounded-xl border border-slate-200 bg-slate-50 focus:bg-white focus:border-indigo-400 outline-none text-sm" />
          </div>
          {(q || dateFrom || dateTo) && (
            <button
              onClick={() => { setQ(""); setDateFrom(""); setDateTo(""); }}
              data-testid="vendor-reports-clear-filters"
              className="inline-flex items-center gap-1.5 h-11 px-3 text-sm font-black text-slate-600 hover:text-slate-900"
            ><X size={14} /> Clear</button>
          )}
          <div className="hidden sm:inline-flex items-center gap-1.5 h-11 px-3 text-[10px] font-black uppercase tracking-widest text-slate-400">
            <Filter size={12} /> Auto-applied
          </div>
        </div>

        {/* List grouped by date */}
        <div className="mt-6 bg-white rounded-2xl border border-slate-200 overflow-hidden">
          {loading ? (
            <div className="p-14 text-center text-sm text-slate-500" data-testid="vendor-reports-loading">
              <Loader2 className="w-6 h-6 mx-auto animate-spin text-slate-400" />
              <div className="mt-2">Loading reports…</div>
            </div>
          ) : items.length === 0 ? (
            <div className="p-14 text-center" data-testid="vendor-reports-empty">
              <FolderOpen className="w-10 h-10 mx-auto text-slate-300" />
              <p className="mt-3 text-slate-500 text-sm">No reports yet — drop your first file into the box above.</p>
            </div>
          ) : (
            <div className="divide-y divide-slate-100">
              {grouped.map(([date, rows]) => (
                <div key={date}>
                  <div className="px-4 sm:px-5 py-2.5 bg-slate-50/70 border-b border-slate-100 flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <span className="inline-flex items-center justify-center w-6 h-6 rounded-md" style={{ backgroundColor: NEXON.mint, color: NEXON.ink }}>
                        <Calendar size={12} />
                      </span>
                      <span className="text-xs uppercase tracking-widest font-black text-slate-700">{fmtDate(date)}</span>
                    </div>
                    <span className="text-[10px] font-black uppercase tracking-widest text-slate-500">
                      {rows.length} {rows.length === 1 ? "report" : "reports"}
                    </span>
                  </div>
                  <ul className="divide-y divide-slate-100">
                    {rows.map((row, i) => {
                      const Icon = iconForFile(row.filename, row.content_type);
                      const tone = toneFor(row.filename);
                      return (
                        <li
                          key={row.id}
                          data-testid={`vendor-report-row-${i}`}
                          className="px-3 sm:px-5 py-4 flex items-start gap-3 hover:bg-slate-50/60 transition-colors"
                        >
                          <div className="w-11 h-11 rounded-xl flex items-center justify-center flex-shrink-0 border" style={{ backgroundColor: tone.bg, borderColor: tone.border, color: tone.fg }}>
                            <Icon className="w-5 h-5" />
                          </div>
                          <div className="min-w-0 flex-1">
                            <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5">
                              <span className="font-black text-sm truncate max-w-[280px] sm:max-w-[420px]" style={{ color: NEXON.ink }}>{row.filename}</span>
                              <span className="text-[10px] font-black uppercase tracking-widest text-slate-400">{fmtSize(row.size)}</span>
                            </div>
                            {row.label && <div className="text-xs text-slate-700 mt-0.5 truncate max-w-[420px]">{row.label}</div>}
                            {row.notes && <div className="text-[11px] text-slate-500 mt-0.5 italic line-clamp-2">{row.notes}</div>}
                            <div className="text-[11px] text-slate-400 mt-1">
                              Uploaded {fmtDateTime(row.uploaded_at)}{row.uploaded_by ? ` · by ${row.uploaded_by}` : ""}
                            </div>
                          </div>
                          <div className="flex items-center gap-1.5 flex-shrink-0">
                            <button
                              onClick={() => onView(row)} data-testid={`vendor-report-view-${i}`}
                              className="inline-flex items-center justify-center w-9 h-9 rounded-lg border border-slate-200 text-slate-700 hover:bg-slate-50 hover:border-indigo-300"
                              aria-label="View report" title="View"
                            ><Eye className="w-4 h-4" /></button>
                            <button
                              onClick={() => onDownload(row)} data-testid={`vendor-report-download-${i}`}
                              className="inline-flex items-center gap-1.5 h-9 px-3 rounded-lg text-white text-xs font-black shadow-sm"
                              style={{ backgroundColor: NEXON.indigo }}
                            ><Download className="w-4 h-4" /><span className="hidden sm:inline">Download</span></button>
                            <button
                              onClick={() => onDelete(row)} data-testid={`vendor-report-delete-${i}`}
                              className="inline-flex items-center justify-center w-9 h-9 rounded-lg border border-slate-200 text-rose-600 hover:bg-rose-50 hover:border-rose-200"
                              aria-label="Delete report" title="Delete"
                            ><Trash2 className="w-4 h-4" /></button>
                          </div>
                        </li>
                      );
                    })}
                  </ul>
                </div>
              ))}
            </div>
          )}
        </div>

        <p className="text-[11px] text-slate-400 px-1 mt-4">
          Files are stored privately in the admin vault. Only signed-in admins can download.
        </p>
      </div>
    </AdminLayout>
  );
}
